// @flow
import {useEffect} from "react";

/**
 * Mounts a brand theme stylesheet for the lifetime of the calling
 * component and detaches it on unmount. Pairs with the webpack rule
 * for `*.lazy.scss` (style-loader injectType "lazyStyleTag"), whose
 * default export exposes `use()` / `unuse()`.
 *
 * Why: brand themes are loaded via dynamic `import()` and webpack
 * never unloads stylesheets it has injected, so navigating between
 * brands previously left every visited theme attached to the DOM —
 * the last-loaded theme then bled across all `#app-root`-scoped
 * variables in every other theme.
 *
 * Caller passes a thunk so the dynamic import is created exactly
 * once per mount, not on every render:
 *
 *   useThemeStylesheet(() => import("./volvo.lazy.scss"));
 */
export default function useThemeStylesheet(load: () => Promise<*>): void {
    useEffect(() => {
        let handle = null;
        let cancelled = false;
        Promise.resolve(load()).then(mod => {
            if (cancelled) return;
            handle = mod && mod.default ? mod.default : mod;
            if (handle && typeof handle.use === "function") {
                handle.use();
            }
        });
        return () => {
            cancelled = true;
            if (handle && typeof handle.unuse === "function") {
                handle.unuse();
            }
        };
        // The loader thunk is intentionally not in deps — each Theme
        // component instance maps to exactly one brand stylesheet.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
